import { Alert, Box, Text, Window, WindowFrame } from "@prismicio/editor-ui"
import type { Variation } from "@prismicio/types-internal/lib/customtypes"

import { SlicePreview } from "../slicePreview"
import type {
  SliceConfigWithSharedSlice,
  VisualSharedSlice,
} from "../sliceTableData"

export type PagePreviewItem =
  | {
      status: "loading"
      slice: SliceConfigWithSharedSlice
    }
  | { status: "done"; slice: VisualSharedSlice }
  | {
      status: "failed"
      slice: SliceConfigWithSharedSlice
      variation: Variation
      html: string
    }

export type PagePreviewItems = Record<string, PagePreviewItem>

interface Props {
  items: PagePreviewItems
  onRetry: (args: { uuid: string }) => void
}

export function PagePreview(props: Props) {
  const { items, onRetry } = props
  return (
    <Box
      overflow="hidden"
      padding={{ block: 12 }}
      flexDirection="column"
      gap={16}
    >
      <Text variant="h3" color="grey12">
        Page preview
      </Text>
      <Window sx={{ flexGrow: 1 }}>
        <WindowFrame />
        <Box
          overflowY="auto"
          flexGrow={1}
          backgroundColor="grey1"
          flexDirection="column"
        >
          {Object.entries(items).map(([uuid, item], index) => (
            <Item
              key={uuid}
              uuid={uuid}
              item={item}
              onRetry={onRetry}
              siblings={getItemSiblings({ index, items })}
            />
          ))}
        </Box>
      </Window>
    </Box>
  )
}

type ItemSibling = PagePreviewItem["status"] | "none"
type ItemSiblings = [ItemSibling, ItemSibling]

function getItemSiblings(args: {
  index: number
  items: PagePreviewItems
}): ItemSiblings {
  const { index, items } = args

  const keys = Object.keys(items)
  const prevKey = index > 0 ? keys[index - 1] : undefined
  const nextKey = index < keys.length - 1 ? keys[index + 1] : undefined

  const prevStatus = (prevKey && items[prevKey]?.status) || "none"
  const nextStatus = (nextKey && items[nextKey]?.status) || "none"

  return [prevStatus, nextStatus]
}

interface ItemProps {
  uuid: string
  item: PagePreviewItem
  onRetry: (args: { uuid: string }) => void
  siblings: ItemSiblings
}

function Item(props: ItemProps) {
  const { uuid, item, onRetry, siblings } = props
  switch (item.status) {
    case "loading":
      return <LoadingItem slice={item.slice} siblings={siblings} />
    case "failed":
      return (
        <ErrorItem
          slice={item.slice}
          uuid={uuid}
          onRetry={onRetry}
          siblings={siblings}
        />
      )
    case "done":
      return <SlicePreview slice={item.slice} />
  }
}

interface LoadingItemProps {
  slice: SliceConfigWithSharedSlice
  siblings: ItemSiblings
}

function LoadingItem(props: LoadingItemProps) {
  const { slice, siblings } = props

  return (
    <Box flexDirection="column" padding={getItemPadding(siblings)}>
      <Alert
        noShadow
        color="info"
        icon="autoFixHigh"
        title={`Generating "${slice.sliceConfig.name}"`}
        titleLoading
      />
    </Box>
  )
}

interface ErrorItemProps {
  slice: SliceConfigWithSharedSlice
  uuid: string
  onRetry: (args: { uuid: string }) => void
  siblings: ItemSiblings
}

function ErrorItem(props: ErrorItemProps) {
  const { slice, uuid, onRetry, siblings } = props

  return (
    <Box flexDirection="column" padding={getItemPadding(siblings)}>
      <Alert
        icon="alert"
        noShadow
        color="error"
        title={`Error generating "${slice.sliceConfig.name}"`}
        rightButton={{
          type: "text",
          title: "Retry",
          onClick: () => onRetry({ uuid }),
        }}
      />
    </Box>
  )
}

const getItemPadding = ([prev, next]: ItemSiblings) => {
  return {
    inline: 16,
    top: prev === "none" || prev === "done" ? 16 : 8,
    bottom: next === "none" || next === "done" ? 16 : 8,
  } as const
}
